'use client'

import { MouseEvent } from 'react'
import { Button } from '@vinted/web-ui'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { followUserEvent } from '@marketplace-web/user-follow/follow-button-data'

import useFollowUser from './useFollowUser'

type Props = {
  userId: number
  isActive: boolean
  username?: string
  isFlat?: boolean
  size?: React.ComponentProps<typeof Button>['size']
  inline?: React.ComponentProps<typeof Button>['inline']
  onFollowToggle?: (payload: { isFollowEvent: boolean }, event: MouseEvent) => void
  testId?: string
}

const FollowButton = ({
  userId,
  username,
  isActive,
  isFlat,
  size,
  inline,
  onFollowToggle,
  testId,
}: Props) => {
  const translate = useTranslate('user.follow_button')
  const { track } = useTracking()

  const { isFollowed, toggleFollow } = useFollowUser({
    userId,
    isFollowed: isActive,
  })

  function handleClick(event: MouseEvent) {
    if (onFollowToggle) {
      onFollowToggle({ isFollowEvent: !isFollowed }, event)
    } else {
      track(followUserEvent({ userId, isFollowEvent: !isFollowed }))
    }

    toggleFollow()
  }

  function getButtonStyling() {
    if (isFlat) return Button.Styling.Flat
    if (isFollowed) return undefined

    return Button.Styling.Filled
  }

  function getText() {
    if (isFollowed) return translate('following')
    if (username) return translate('follow_username', { username })

    return translate('follow')
  }

  return (
    <Button
      size={size}
      styling={getButtonStyling()}
      onClick={handleClick}
      text={getText()}
      inline={inline}
      testId={testId}
    />
  )
}

export default FollowButton
